import { exportLearnersReportApi, useLearnersForReport, useLearningProgressIndustries } from "@/api/services/main/learning-progress";
import { TLearnerForReportOutputDto } from "@/api/services/main/learning-progress/dtos/get-learners-for-report.dto";
import { useCompanyInfo } from "@/api/services/main/company";
import { FooterContainer } from "@/components/commons/FooterContainer";
import { Button } from "@/components/elements/Button";
import { InnerBox } from "@/components/elements/InnerBox";
import { Pagination } from "@/components/elements/Pagination";
import { Table, TableSorting } from "@/components/elements/Table";
import { IconFileExportFill } from "@/assets/icons";
import { EMPTY_STR } from "@/constants/string";
import { DATE_FORMAT_FOR_API, DATE_FORMAT_FOR_FILENAME } from "@/constants/datetime";
import { DEFAULT_PER_PAGE, START_PAGE } from "@/constants/pagination";
import { usePagination } from "@/hooks/usePagination";
import { useSelectionTable } from "@/hooks/useSelectionTable";
import { LearnerReportFilterModal } from "@/features/learning-progress/components/report/LearnerReportFilterModal";
import { ReportFilterBar } from "@/features/learning-progress/components/report/ReportFilterBar";
import {
  ReportPeriodField,
  TReportPeriodForm,
  getReportPeriodDefaults,
  reportPeriodSchema,
} from "@/features/learning-progress/components/report/ReportPeriodField";
import {
  TLearnerReportFilterForm,
  buildLearnerReportFilterApiParams,
  countActiveLearnerReportFilters,
  defaultLearnerReportFilterValues,
} from "@/features/learning-progress/utils/learner-report-filter-shared";
import { GENDER_LABEL, TGender } from "@/types/enum";
import { button, divider, text } from "@/theme/colors";
import { showError } from "@/utils/error";
import { AppRoutes } from "@/routes/config";
import { downloadBlobAsFile, generateDocxBlobFromTemplate } from "@/utils/docxTemplate";
import { getDynamicRoute } from "@/utils/url";
import { toast } from "@/utils/toast";
import { Box, Stack, Typography } from "@mui/material";
import dayjs from "dayjs";
import { MRT_ColumnDef } from "material-react-table";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { FC, useMemo, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

const LEARNING_PROGRESS_REPORT_TEMPLATE_PATH = "/templates/learning-progress/学習進捗報告書_報告対象期間選択.docx";
// Windows-illegal filename chars per Win32 spec; replaced with underscore to avoid save failures.
const WINDOWS_ILLEGAL_FILENAME_CHARS = /[\\/:*?"<>|]/g;
const sanitizeFilename = (raw: string): string => raw.replace(WINDOWS_ILLEGAL_FILENAME_CHARS, "_");

export const LearningProgressReportCompanyPage: FC = () => {
  const { industryId, companyId } = useParams<{ industryId: string; companyId: string }>();
  const navigate = useNavigate();
  const [, setSearchParams] = useSearchParams();
  const pagReq = usePagination();
  const queryClient = useQueryClient();

  const numericIndustryId = industryId ? Number(industryId) : 0;
  const numericCompanyId = companyId ? Number(companyId) : 0;
  const currentPage = pagReq.page || START_PAGE;
  const perPage = pagReq.perPage || DEFAULT_PER_PAGE;

  const { data: companyData } = useCompanyInfo(numericCompanyId);
  const company = companyData?.data;
  const companyName = company?.companyName ?? EMPTY_STR.TEXT;

  const { data: industriesData } = useLearningProgressIndustries();
  const industry = industriesData?.data?.find((item) => item.id === numericIndustryId);
  const industryName = industry?.name ?? EMPTY_STR.TEXT;

  const [filters, setFilters] = useState<TLearnerReportFilterForm | null>(null);
  const [isExporting, setIsExporting] = useState(false);

  const reportPeriodMethods = useForm<TReportPeriodForm>({
    defaultValues: getReportPeriodDefaults(),
    resolver: yupResolver(reportPeriodSchema) as unknown as never,
    mode: "all",
    reValidateMode: "onBlur",
  });
  const periodFrom = useWatch({ control: reportPeriodMethods.control, name: "periodFrom" });
  const periodTo = useWatch({ control: reportPeriodMethods.control, name: "periodTo" });
  // 報告対象期間が未入力またはバリデーションエラー中は報告書を作成できない。
  const isPeriodValid = reportPeriodMethods.formState.isValid;

  const { data: learnersData, isLoading } = useLearnersForReport({
    industryId: numericIndustryId,
    companyId: numericCompanyId,
    page: currentPage,
    perPage,
    sorts: pagReq.sorts,
    ...buildLearnerReportFilterApiParams(filters),
  });

  const rows = learnersData?.data ?? [];
  const pagination = learnersData?.pagination;

  const resetToFirstPage = () => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("page", "1");
        return next;
      },
      { replace: true },
    );
  };

  // 性別は仕様上ソート対象外。他の表示カラムは TableSorting で sort トグル有効。
  const columns = useMemo<MRT_ColumnDef<TLearnerForReportOutputDto>[]>(
    () => [
      {
        id: "fullName",
        header: "氏名",
        accessorKey: "fullName",
        size: 619,
        Header: <TableSorting name="learner.fullName">氏名</TableSorting>,
        Cell: ({ row }) => row.original.fullName || EMPTY_STR.TEXT,
      },
      {
        id: "nationality",
        header: "国籍",
        accessorKey: "nationality.nationalityName",
        size: 116,
        Header: <TableSorting name="nationality.nationalityName">国籍</TableSorting>,
        Cell: ({ row }) => row.original.nationality?.nationalityName || EMPTY_STR.TEXT,
      },
      {
        id: "gender",
        header: "性別",
        accessorKey: "gender",
        size: 60,
        Cell: ({ row }) => (row.original.gender ? GENDER_LABEL[row.original.gender as TGender] : EMPTY_STR.TEXT),
      },
      {
        id: "prefecture",
        header: "居住地",
        accessorKey: "prefecture",
        size: 106,
        Header: <TableSorting name="learner.prefecture">居住地</TableSorting>,
        Cell: ({ row }) => row.original.prefecture || EMPTY_STR.TEXT,
      },
      {
        id: "remainingPeriod",
        header: "1号残期間",
        accessorKey: "remainingPeriod",
        size: 129,
        Header: <TableSorting name="learner.clause1StartDate">1号残期間</TableSorting>,
        Cell: ({ row }) => row.original.remainingPeriod || EMPTY_STR.TEXT,
      },
    ],
    [],
  );

  const { table, rowSelectionKeys, clearSelection } = useSelectionTable({
    primaryKey: "id",
    columns,
    rows,
    enableRowSelection: true,
    pinningColumnsLeft: ["mrt-row-select"],
    renderEmptyRowsFallback: () => <Box />,
  });

  // 選択キー(= 学習者ID の文字列)をそのまま出力対象IDに変換する。ページを跨いだ選択も含まれる。
  // 非数値キーは NaN になり得るため除外し、不正なIDが export ペイロードへ混入しないよう防御する。
  const selectedLearnerIds = useMemo(() => rowSelectionKeys.map(Number).filter(Number.isFinite), [rowSelectionKeys]);

  // フィルタ適用/解除時のみ選択をリセットする (対象データ集合が変わるため)。ページ送り・ソートでは維持する。
  const handleApplyFilters = (next: TLearnerReportFilterForm) => {
    clearSelection();
    setFilters(countActiveLearnerReportFilters(next) > 0 ? next : null);
    resetToFirstPage();
  };

  const handleExport = async () => {
    if (selectedLearnerIds.length === 0 || isExporting || !periodFrom || !periodTo || !isPeriodValid) return;
    try {
      setIsExporting(true);
      const res = await exportLearnersReportApi(
        numericIndustryId,
        numericCompanyId,
        selectedLearnerIds,
        periodFrom.format(DATE_FORMAT_FOR_API),
        periodTo.format(DATE_FORMAT_FOR_API),
      );

      const { header, aggregates, learners, periodAggregates, periodLearners } = res.data;
      // period* fields are already prefixed, so spreading both aggregate objects is collision-free.
      const templateValues = { ...header, ...aggregates, ...periodAggregates, learners, periodLearners };
      const blob = await generateDocxBlobFromTemplate(LEARNING_PROGRESS_REPORT_TEMPLATE_PATH, templateValues);
      if (!blob) {
        throw new Error("テンプレートの取得に失敗しました。");
      }

      const safeCompanyName = sanitizeFilename(companyName || String(numericCompanyId));
      const dateSuffix = dayjs().format(DATE_FORMAT_FOR_FILENAME);
      downloadBlobAsFile(blob, `学習進捗報告書_${safeCompanyName}_${dateSuffix}.docx`);
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_PROGRESS.FETCH_COMPANIES_FOR_REPORT] });
      toast.success("報告書を作成しました。");
    } catch (e) {
      showError(e);
    } finally {
      setIsExporting(false);
    }
  };

  const handleGoBack = () =>
    navigate(getDynamicRoute(AppRoutes.learningProgressReport, { industryId: String(numericIndustryId) }));

  return (
    <Stack gap={4}>
      <Stack gap={2}>
        <Typography variant="head20Bold">学習進捗報告書の作成 - {industryName}</Typography>
      </Stack>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={3}>
          <Stack gap={0.5}>
            <Typography variant="cap12" color={text.secondary}>
              所属機関
            </Typography>
            <Typography variant="body14Bold">{companyName}</Typography>
          </Stack>
          <ReportPeriodField methods={reportPeriodMethods} />
        </Stack>
      </InnerBox>

      <InnerBox padding={3} showDivider={false} bordered={true} borderColor={divider.high}>
        <Stack gap={2}>
          <Stack direction="row" alignItems="center" gap={1.5}>
            <Typography variant="body14Semi">
              全 {(pagination?.total ?? 0).toLocaleString()} 件中 {Math.min(currentPage * perPage, pagination?.total ?? 0)} 件を表示
            </Typography>
            <ReportFilterBar
              activeFilterCount={countActiveLearnerReportFilters(filters)}
              onApplyFilters={handleApplyFilters}
              clearValues={defaultLearnerReportFilterValues}
              renderModal={({ anchorEl, onClose, onApplyFilters: onApply }) => (
                <LearnerReportFilterModal
                  anchorEl={anchorEl}
                  onClose={onClose}
                  onApplyFilters={onApply}
                  initialFilters={filters ?? defaultLearnerReportFilterValues}
                />
              )}
            />
          </Stack>

          <Table table={table} isLoading={isLoading} height={"calc(100vh - 600px)"} minHeight={150} />
          <Pagination totalPage={pagination?.totalPage ?? 1} />
        </Stack>
      </InnerBox>

      <FooterContainer>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Button variant="ghost" size="sm" onClick={handleGoBack}>
            戻る
          </Button>
          <Stack direction="row" alignItems="center" gap={3}>
            <Typography variant="cap12Bold" fontWeight={500}>
              選択した学習者数{" "}
              <Typography component="span" variant="sub16Bold" sx={{ color: button.primary, mx: 0.5 }}>
                {selectedLearnerIds.length}
              </Typography>
              名
            </Typography>
            <Button
              variant="primary"
              size="sm"
              startIcon={<IconFileExportFill />}
              disabled={selectedLearnerIds.length === 0 || isExporting || !periodFrom || !periodTo || !isPeriodValid}
              onClick={handleExport}
            >
              報告書を作成
            </Button>
          </Stack>
        </Stack>
      </FooterContainer>

    </Stack>
  );
};
